import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const Portfolio = () => {
  const { t } = useLanguage();
  const topics = t.portfolio.topics;
  const [active, setActive] = useState(0);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-3xl">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#B87333]" />
                <span className="overline text-[#B87333]">{t.portfolio.overline}</span>
              </div>
              <h2 className="font-display text-[#0B1F33] font-extrabold tracking-tighter leading-[1.02] text-4xl lg:text-5xl">
                {t.portfolio.title}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#6B7280] text-sm max-w-sm lg:text-right">{t.portfolio.note}</p>
          </Reveal>
        </div>

        <div className="mt-16 border-t border-[#E5E7EB]">
          {topics.map((topic, i) => {
            const open = active === i;
            return (
              <div key={topic.id} className="border-b border-[#E5E7EB]">
                <button
                  data-testid={`topic-toggle-${i}`}
                  onClick={() => setActive(open ? -1 : i)}
                  className="group w-full flex items-center justify-between gap-6 py-7 text-left"
                >
                  <div className="flex items-center gap-5 lg:gap-8">
                    <span className="font-display text-sm font-bold text-[#B87333] tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className={`font-display text-xl lg:text-3xl font-bold tracking-tight transition-colors duration-300 ${open ? "text-[#0B1F33]" : "text-[#0B1F33]/70 group-hover:text-[#0B1F33]"}`}>
                      {topic.title}
                    </h3>
                  </div>
                  <span className={`shrink-0 grid place-items-center w-10 h-10 border transition-colors duration-300 ${open ? "bg-[#0B1F33] text-white border-[#0B1F33]" : "border-[#E5E7EB] text-[#0B1F33] group-hover:border-[#B87333]"}`}>
                    {open ? <Minus size={18} /> : <Plus size={18} />}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      data-testid={`topic-content-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 pb-12 pt-2 items-stretch">
                        <div className="lg:col-span-6 order-2 lg:order-1">
                          <p className="text-[#4b5563] text-base leading-relaxed max-w-xl">
                            {topic.text}
                          </p>
                          <div className="mt-8">
                            {topic.partners.length > 0 && (
                              <>
                            <div className="overline text-[#9ca3af] mb-4">{topic.partnersLabel}</div>
                            <div className="flex flex-wrap items-center gap-4">
                              {topic.partners.map((p) => (
                                <div
                                  key={p.name}
                                  title={p.name}
                                  className="h-16 w-32 grid place-items-center bg-white border border-[#E5E7EB] px-4 hover:border-[#B87333] transition-colors duration-300"
                                >
                                  <img
                                    src={p.src}
                                    alt={`${p.name} logo`}
                                    loading="lazy"
                                    className="max-h-10 max-w-full object-contain"
                                  />
                                </div>
                              ))}
                            </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="lg:col-span-6 order-1 lg:order-2">
                          <div className="overflow-hidden h-64 lg:h-full lg:min-h-[280px] bg-[#0B1F33]">
                            <img
                              src={topic.image}
                              alt={topic.title}
                              className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-700"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
